import {
	ALLOW_DOWNLOAD_FORCED,
	ALLOW_DOWNLOAD_OPTIONAL,
	AudioPreview,
	AudioSectionContainer,
	CollectionItem,
	Collection,
	Coordinates,
	INITIAL_LAYOUT_MAP,
	INITIAL_LAYOUT_SINGLE_PAGE,
	PlayerStatus,
	PlaylistItem,
	SCREEN_SECTION_TYPE_AUDIO,
} from 'mytours-read-common'

import { setDataToLocalStorage, getDataFromLocalStorage, removeDataFromLocalStorage } from '../platform'
import { formatBytes } from '../offline/functions'
import type { DownloadStatus } from '../offline/useDownload'
import strings from '../../strings'

export type LayoutMode = 'default' | 'map' | 'singlePage'

/**
 * Which of the three tour layouts a collection asks for. Anything unrecognised — including an
 * absent value — is the default layout.
 */
export function layoutMode(initialLayout?: string): LayoutMode {
	if (initialLayout === INITIAL_LAYOUT_MAP) {
		return 'map'
	}

	if (initialLayout === INITIAL_LAYOUT_SINGLE_PAGE) {
		return 'singlePage'
	}

	return 'default'
}

export type WebDownloadPolicy = 'off' | 'optional' | 'forced'

/**
 * Whether this collection offers an offline download on web, and how hard it insists.
 *
 * `allow_download` is shared with native, which has a fourth value — `forced_native` — that builder
 * stamps on every tour it creates. Reading it as anything but `off` would switch this on for every
 * existing tour rather than the ones a customer chose. Anything unrecognised is `off` for the same
 * reason a gate we cannot open is worse than no gate: the visitor keeps the tour they can start.
 */
export function webDownloadPolicy(allowDownload?: string): WebDownloadPolicy {
	if (allowDownload === ALLOW_DOWNLOAD_OPTIONAL) {
		return 'optional'
	}

	if (allowDownload === ALLOW_DOWNLOAD_FORCED) {
		return 'forced'
	}

	return 'off'
}

export interface DownloadRowState {
	downloading: boolean
	failed: boolean
	saved: boolean
	/** A tour saved in another language is not this tour saved: every URL it stored is that language's. */
	otherLanguage: boolean
}

/** What a download's registry state means to the surface offering it, shared by the layouts that do. */
export function downloadRowState(
	status: DownloadStatus,
	savedLanguage: string | undefined,
	language: string,
): DownloadRowState {
	const otherLanguage = status === 'complete' && savedLanguage !== undefined && savedLanguage !== language

	return {
		downloading: status === 'downloading' || status === 'queued',
		failed: status === 'incomplete' || status === 'error',
		saved: status === 'complete' && !otherLanguage,
		otherLanguage,
	}
}

export const downloadGateLabel = (fileSize?: number): string => `${strings.download_tour} · ${formatBytes(fileSize)}`

/** The floating pill's own offer. Shorter than the gate's: the pill is alone on the page it floats over. */
export const downloadPillLabel = (state: DownloadRowState, fileSize?: number): string =>
	state.downloading || state.saved || state.failed || state.otherLanguage
		? downloadLabel(state, fileSize)
		: `${strings.download} · ${formatBytes(fileSize)}`

const DOWNLOAD_PILL_KEY = 'stqry.download-pill-dismissed.'

// Read synchronously rather than through platform's promise-based storage, which would render the pill
// for a frame before putting it away — the one thing a remembered dismissal is there to stop.
export const downloadPillDismissed = (collectionId: number): boolean => {
	try {
		return localStorage.getItem(`${DOWNLOAD_PILL_KEY}${collectionId}`) === 'true'
	} catch {
		return false
	}
}

export const dismissDownloadPill = (collectionId: number): void => {
	try {
		localStorage.setItem(`${DOWNLOAD_PILL_KEY}${collectionId}`, 'true')
	} catch {
		// A browser that refuses storage keeps the collapsed circle for this reading and offers the pill
		// again next visit, which is the safe way round.
	}
}

/** What to call the control in each state. The size belongs only to the offer, not to what follows it. */
export function downloadLabel(state: DownloadRowState, fileSize?: number): string {
	if (state.downloading) return strings.downloading_tour
	if (state.saved) return strings.downloaded_tour
	if (state.failed) return strings.download_incomplete
	if (state.otherLanguage) return strings.download_new_language

	return downloadGateLabel(fileSize)
}

// The open stop rides on the tour URL as `#stop-<item id>`, beside the `#tour-map`, `#list-map` and
// `#qr-code` hashes the app already speaks. The item id rather than the collection item's, because that
// is what a QR code, a keypad entry and a stop route already address a stop by.
const STOP_HASH_PREFIX = '#stop-'

/** The hash that opens `stopId` on a single page tour. */
export function stopHash(stopId: string | number): string {
	return `${STOP_HASH_PREFIX}${stopId}`
}

/**
 * The stops a download should fetch next: the one the visitor just opened and the two behind it.
 *
 * Three because that is roughly as far ahead as a visitor walks while a stop's audio downloads — enough
 * that they stay behind the downloader, few enough that re-ordering on every tap does not undo the tour
 * order the queue was built in. `null` means the tour has just started, so the first stops are the ones.
 */
export function stopsToPrioritize(items?: CollectionItem[], fromStopId?: string | null): number[] {
	if (!items?.length) {
		return []
	}

	const start = fromStopId ? items.findIndex(item => String(item.item_id) === String(fromStopId)) : 0

	return items
		.slice(start === -1 ? 0 : start, (start === -1 ? 0 : start) + 3)
		.map(item => Number(item.item_id))
		.filter(id => Number.isFinite(id))
}

/** The stop a hash opens, or null for every hash that opens none — the map's and the scanner's included. */
export function stopIdFromHash(hash?: string): string | null {
	if (!hash?.startsWith(STOP_HASH_PREFIX)) {
		return null
	}

	return hash.slice(STOP_HASH_PREFIX.length) || null
}

/**
 * A track and the section that holds it: what a stop's pill needs to be a player, whether the pill is
 * there because the stop leads with audio or because it is the stop with a track going.
 */
export interface StopAudioSection {
	audio: AudioPreview
	container: AudioSectionContainer
}

/**
 * The audio a stop leads with: the *first* section of its screen, and only when that section is audio.
 * A stop whose screen opens with an image or a quiz has none, even if it carries audio further down —
 * the collapsed row advertises what the stop leads with, not everything it contains.
 *
 * Returns the container alongside, because every `soundController` lookup is keyed on the section that
 * holds the track, not on the stop.
 *
 * This is a stop's *default* — what its collapsed row looks like when the page has nothing of its own
 * going. `stopActiveAudio` is the other half; between them they decide whether a row gets a play
 * control at all and what that control plays, so the two cannot disagree.
 */
export function stopLeadingAudio(stop: CollectionItem): StopAudioSection | undefined {
	const section = stop.screen?.sections?.[0]
	if (!section) {
		return undefined
	}

	// A `media` section carries its single item on `media_item`; a `media_group` carries a list.
	const audio = (section.media_item ?? section.section_items?.[0]) as AudioPreview | undefined

	// No file means nothing to register a sound under, so there is nothing a control could play.
	if (audio?.media_type !== SCREEN_SECTION_TYPE_AUDIO || !audio.file) {
		return undefined
	}

	return { audio, container: { type: 'section', id: section.id, title: section.title } }
}

/**
 * The sections of a stop's screen that hold a playable track, by id.
 *
 * A section is what playback is keyed on — `AudioSectionContainer` is `{ type: 'section', id }` — so
 * these are how a stop's tracks are recognised in the audio store, which knows about sections and
 * nothing about stops. Used to answer "is the track the page is playing this stop's?".
 *
 * Unlike `stopLeadingAudio` this looks at the whole screen, because an expanded stop shows a transport
 * for audio wherever it sits, not only for what the collapsed row advertises.
 */
export function stopAudioSectionIds(stop?: CollectionItem): number[] {
	const sections = stop?.screen?.sections ?? []

	return sections
		.filter(section => {
			const items = section.section_items ?? (section.media_item ? [section.media_item] : [])

			return items.some(item => item?.media_type === SCREEN_SECTION_TYPE_AUDIO && Boolean(item.file))
		})
		.map(section => section.id)
}

/**
 * The track this stop has going: the current one, playing or paused part-way through, and only when it
 * is this stop's. Nothing otherwise.
 *
 * The collapsed row's pill is the single page layout's only player — there is no bar behind it — so the
 * stop the reader can hear has to carry a control whether or not that is what it leads with. Read off
 * the current playlist entry rather than remembered: nothing anywhere records that a stop *was* playing,
 * so nothing has to unwind it. When this goes false the row falls back to `stopLeadingAudio` — a play
 * pill for a stop that leads with audio, and a plain thumbnail for one that does not.
 *
 * Paused counts, and that is the whole reason this is not simply "playing". A paused track is one the
 * reader stopped half way and can resume, and on a stop that leads with something else the pill is the
 * only control there is: dropping it on pause would take the control away at the exact moment it was
 * used, leaving a stop paused mid-track with nothing to press. Stopped and ended do not count — another
 * stop starting stops this one, and a track that ran out has nothing to resume.
 *
 * Anything not held by a section is not a stop's: a tour's own audio preview is keyed on the tour.
 */
export function stopActiveAudio(stop: CollectionItem, current?: PlaylistItem): StopAudioSection | undefined {
	const isActive = current?.playerStatus === PlayerStatus.Playing || current?.playerStatus === PlayerStatus.Pause

	if (!isActive || current.container?.type !== 'section') {
		return undefined
	}

	const audio = current.audioTrack?.audio
	if (!audio?.file || !stopAudioSectionIds(stop).includes(current.container.id)) {
		return undefined
	}

	return { audio, container: { type: 'section', id: current.container.id, title: current.container.title } }
}

/**
 * Whether the current track is this tour's: held by a section of one of its stops, or by the tour
 * itself, which is how its own audio preview is keyed.
 *
 * Asked by the single page layout to answer the opposite question — whether the sound the app is
 * playing came from somewhere else entirely, another tab or another screen. Every track this tour owns
 * already has a control on the page (a stop's pill, or the preview's own player above the list), and a
 * track it does not own has none, which is the one case that still wants the sticky bar.
 *
 * A track held by a section this tour does not carry is another screen's. A tour container with someone
 * else's id is another tour's preview. Both are foreign.
 *
 * Before the stops arrive nothing can be claimed by a section, so a stop's own track would read as
 * foreign — which is the right way round: no stops means no pills, and a bar beats no control at all.
 */
export function tourOwnsAudio(
	current: PlaylistItem | undefined,
	collectionId: number,
	collectionItems?: CollectionItem[],
): boolean {
	const container = current?.container
	if (!container) {
		return false
	}

	if (container.type === 'tour') {
		return container.id === collectionId
	}

	if (container.type !== 'section') {
		return false
	}

	return (collectionItems ?? []).some(stop => stopAudioSectionIds(stop).includes(container.id))
}

export function saveCollectionItemsToDisk(
	projectId: string,
	collectionId: number,
	collectionVersion: number,
	language: string,
	items: CollectionItem[] | undefined,
): Promise<void> {
	const key = projectId + '_collection_' + collectionId + '_' + language + '_' + collectionVersion
	return setDataToLocalStorage(key, items)
}

/**
 * Attempts to load collection items from disk. Parses into CollectionItem[] type.
 */
export function loadCollectionItemsFromDisk(
	projectId: string,
	collectionId: number,
	collectionVersion: number,
	language: string,
): Promise<CollectionItem[] | undefined> {
	const key = projectId + '_collection_' + collectionId + '_' + language + '_' + (collectionVersion || '')
	return getDataFromLocalStorage(key, false, !collectionVersion)
}

/**
 * Removes any collection items that are persisted on disk.
 */
export function removeCollectionItemsFromDisk(
	projectId: string,
	collectionId: number,
	language: string,
): Promise<void> {
	const key = projectId + '_collection_' + collectionId + '_' + language + '_'
	return removeDataFromLocalStorage(key)
}

/**
 * Saves collection details to disk so can be loaded faster next time without hitting network.
 *
 * @param collection details to save to disk.
 */
export function saveCollectionDetailsToDisk(
	projectId: string,
	collectionId: number,
	collectionVersion: number,
	language: string,
	collection: Collection,
): Promise<void> {
	const key = projectId + '_collection_details_' + collectionId + '_' + language + '_' + collectionVersion
	return setDataToLocalStorage(key, collection)
}

/**
 * Attempts to load collection details from disk. Parses into Collection type.
 */
export function loadCollectionDetailsFromDisk(
	projectId: string,
	collectionId: number,
	collectionVersion: number,
	language: string,
): Promise<Collection | undefined> {
	const key = projectId + '_collection_details_' + collectionId + '_' + language + '_' + (collectionVersion || '')
	return getDataFromLocalStorage(key, false, !collectionVersion)
}

/**
 * Removes any collection details that are persisted on disk.
 */
export function removeCollectionDetailsFromDisk(
	projectId: string,
	collectionId: number,
	language: string,
): Promise<void> {
	const key = projectId + '_collection_details_' + collectionId + '_' + language + '_'
	return removeDataFromLocalStorage(key)
}

/**
 * Saves map route to disk so can be loaded faster next time without hitting network.
 */
export function saveMapRouteToDisk(
	projectId: string,
	mapRouteId: number,
	mapRouteVersion: number,
	language: string,
	mapRoute: any[],
): Promise<void> {
	const key = projectId + '_map_route_' + mapRouteId + '_' + language + '_' + mapRouteVersion
	return setDataToLocalStorage(key, mapRoute)
}

/**
 * Attempts to load map route from disk. Parses into MapRoute type.
 */
export function loadMapRouteFromDisk(
	projectId: string,
	mapRouteId: number,
	mapRouteVersion: number,
	language: string,
): Promise<any | undefined> {
	const key = projectId + '_map_route_' + mapRouteId + '_' + language + '_' + mapRouteVersion
	return getDataFromLocalStorage(key)
}

/**
 * Removes any map route that are persisted on disk.
 */
export function removeMapRouteFromDisk(projectId: string, mapRouteId: number, language: string): Promise<void> {
	const key = projectId + '_map_route_' + mapRouteId + '_' + language + '_'
	return removeDataFromLocalStorage(key)
}

// copied this code from react-navigation-collapsible
export function getStatusBarHeight() {
	return 0
}

export const getUserCurrentPosition = (
	successCallback: (position: Coordinates) => void,
	errorCallback?: (error: { code: number; message: string }) => void,
): void =>
	navigator.geolocation.getCurrentPosition(
		successResult => {
			successCallback(successResult.coords)
		},
		error => {
			console.warn('get current position error', error)
			errorCallback?.(error)
		},
		{
			enableHighAccuracy: true,
			timeout: 10000,
		},
	)
